import type { MarketplaceOrderBrief } from './orderBrief';
import type { MarketplaceOrderDeliverable } from './marketplaceOrderDeliverable';
import type { Timestamp } from './common';
import type { MarketplaceSubmissionUnit } from './marketplaceSubmission';

/** Firestore `marketplaceOrders.status` — marketplace order lifecycle. */
export type MarketplaceOrderStatus =
  | 'pendingCreatorAcceptance'
  | 'pendingCompletion'
  | 'completed'
  | 'creatorRejected'
  | 'adminCanceled'
  | 'creatorCountered'
  | 'inviteExpired'
  | 'pendingBrandApproval';

export interface MarketplaceOrderCounterLog {
  action: 'counter' | 'update';
  by: 'creator' | 'brand';
  cash: number;
  deliverables?: Record<string, MarketplaceOrderDeliverable>;
  reason?: string;
  at: Timestamp;
}

export interface MarketplaceOrderStatusLogEntry {
  at: Timestamp;
  fromStatus?: MarketplaceOrderStatus;
  toStatus: MarketplaceOrderStatus;
}

export interface MarketplaceOrderExtensionRequest {
  requestedAt: Timestamp;
  message?: string;
  status: 'pending' | 'accepted' | 'rejected';
  respondedAt?: Timestamp;
}

/**
 * Firestore `marketplaceOrders/{id}` document — field names match stored documents (`brandID`, `influencerID`).
 */
export interface MarketplaceOrder {
  influencerID: string;
  brandID: string;
  cash: number;
  status: MarketplaceOrderStatus;
  orderBrief?: MarketplaceOrderBrief;
  deliverables?: Record<string, MarketplaceOrderDeliverable>;
  submissions?: Record<string, MarketplaceSubmissionUnit>;
  createdAt?: Timestamp;
  inviteExpiresAt?: Timestamp;
  inviteExpiredAt?: Timestamp;
  inviteExtensionRequest?: MarketplaceOrderExtensionRequest;
  completedAt?: Timestamp;
  canceledAt?: Timestamp;
  cancelDisputeId?: string;
  cancelReason?: string;
  creatorProposedCash?: number;
  creatorProposedDeliverables?: Record<string, MarketplaceOrderDeliverable>;
  creatorCounterReason?: string;
  brandRejectedCounter?: boolean;
  brandCounteredBack?: boolean;
  brandCounterReason?: string;
  counterLogs?: MarketplaceOrderCounterLog[];
  statusLogs?: MarketplaceOrderStatusLogEntry[];
  /** Set at creation when the brand opts-in to allow applications if the creator declines/expires. */
  allowApplications?: boolean;
  /** Set when a MarketplaceOpportunity is created from this order. */
  opportunityId?: string;
  /** True once the creator payout has been transferred to their Whop wallet. */
  payoutClaimed?: boolean;
  /** Timestamp when the payout transfer was confirmed. */
  payoutClaimedAt?: Timestamp;
  /** Whop transfer id when returned by the API; omitted on idempotent replay. Idempotence key is lc_order_transfer_{id}. */
  whopTransferId?: string;
}

export const MAX_MARKETPLACE_COUNTERS = 1;
